import { ApiService } from "@app/services/api/api.service";
import { DataStorageService } from "@app/services/data-storage/data-storage.service";
import { VesselGroupsService } from "@app/services/vessel-groups/vessel-groups.service";
import { Subscription } from "rxjs";
import { Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import * as fileManager from "assets/webix/codebase/filemanager.js";
import { FilesService } from "@app/services/hull-condition/files.service";
import { MessageService } from "primeng/api";

declare const webix: any;

interface ServerFile {
  children: Array<ServerFile>;
  lastModifiedAt_UTC: string;
  path: string;
  size_byte: number;
  type: string;
}

interface File {
  data: Array<File>;
  date: Date;
  id: string;
  size: number;
  type: string;
  value: string;
}

function filename(path: string): string {
  const separator = path.lastIndexOf("/");
  return separator >= 0 && path.length > separator + 1 ? path.substring(separator + 1) : path;
}

function mapFile(serverFile: ServerFile): File {
  return {
    data: mapFileContents(serverFile.children),
    date: new Date(serverFile.lastModifiedAt_UTC),
    id: "/" + serverFile.path,
    size: serverFile.size_byte,
    type: serverFile.type.toLowerCase(),
    value: filename(serverFile.path),
  };
}

function mapFileContents(contents: Array<ServerFile>): Array<File> {
  return contents.map((content) => mapFile(content)).sort(fileComparator);
}

function fileComparator(a: File, b: File) {
  if (a.type === "folder" && b.type !== "folder") {
    return -1;
  }
  if (a.type !== "folder" && b.type === "folder") {
    return 1;
  }
  return a.value.localeCompare(b.value);
}

class FileManagerAdapter extends fileManager.services.Backend {
  apiService: ApiService = null;
  selectedVessel: any;
  storage: any;
  storageComponent: DataStorageComponent;

  private readonly errorHandler = (err) => {
    this.storageComponent.showError(err.error.message);
    return {
      invalid: true,
      error: err.error.message,
      id: "",
    };
  };

  private readonly showSpinner = (show: boolean) => {
    this.storageComponent && (this.storageComponent.showSpinner = show);
  };

  // https://docs.webix.com/filemanager__backend.html#backendservicemethods

  // Copies a file or directory to the new path and returns the data of the copied object
  // POST copy
  // copy(fromPath, toPath) {
  //   return Promise.resolve({
  //     "value":"write.txt","id":"/new directory/write.txt","size":42,
  //     "date":1584635434,"type":"code"
  //   });
  // }

  // Returns the full path to download a file.
  directLink(filePath: string) {
    if (!this.selectedVessel) {
      return "";
    }
    return this.apiService.resolveLink(this.storage._links["fo:download"].href, {
      file: [filePath],
    });
  }

  // Returns the files/folders in a folder.
  files(folderPath: string) {
    if (!this.selectedVessel || !this.storage?._links) {
      return Promise.resolve([]);
    }
    this.showSpinner(true);
    const hrefFiles = this.apiService.resolveLink(this.storage._links["fo:files"].href, {
      folder: [folderPath],
    });
    return this.apiService
      .get(hrefFiles)
      .toPromise()
      .then((serverFiles) => {
        return serverFiles._embedded ? serverFiles._embedded["fo:files"].map((serverFile) => mapFile(serverFile)).sort(fileComparator) : [];
      })
      .catch(this.errorHandler)
      .finally(() => this.showSpinner(false));
  }

  // Returns all folders recursively
  folders(folderPath: string) {
    if (!this.selectedVessel || !this.storage?._links) {
      return Promise.resolve([]);
    }
    this.showSpinner(true);
    const hrefFiles = this.apiService.resolveLink(this.storage._links["fo:folders"].href, { folder: folderPath });
    return this.apiService
      .get(hrefFiles)
      .toPromise()
      .then((serverFiles) => {
        return serverFiles._embedded ? serverFiles._embedded["fo:files"].map((serverFile) => mapFile(serverFile)).sort(fileComparator) : [];
      })
      .catch(this.errorHandler)
      .finally(() => this.showSpinner(false));
  }

  // Returns the data of the root directory (total, free, and used space in bytes)
  // and if the server supports preview and metadata for specific files.
  getInfo() {
    return Promise.resolve({
      stats: {
        free: this.storage?.totalSpace_bytes - this.storage?.consumedSpace_bytes || 0,
        total: this.storage?.totalSpace_bytes ?? 0,
        used: this.storage?.consumedSpace_bytes ?? 0,
      },
      features: {
        preview: {
          code: false,
          document: false,
          image: false,
        },
        meta: {
          audio: false,
          image: false,
        },
      },
    });
  }

  // Do not implement. Returns metadata for image and audio files.
  // getMeta(filePath) {
  //   return Promise.resolve({
  //     "Title":"IAMX - Little Deaths","Artist":"IAMX",
  //     "Album":"Unfall","Year":"2017","Genre":"Electronic"
  //   });
  // }

  // Return the heading of the widget.
  getRootName() {
    return "Data Storage";
  }

  // Creates a folder.
  makedir(parentFolderPath: string, newFolderName: string) {
    if (parentFolderPath.startsWith("/")) {
      parentFolderPath = parentFolderPath.substring(1);
    }
    const hrefFiles = this.apiService.resolveLink(this.storage._links["fo:create-folder"].href, { folder: parentFolderPath + "/" + newFolderName });
    return this.apiService
      .post(hrefFiles, null)
      .toPromise()
      .then((serverFile) => {
        return mapFile(serverFile);
      })
      .catch(this.errorHandler);
  }

  // Maybe implement. Creates an empty file that can be edited.
  // POST create-file
  // makefile(parentFolderPath, fileName) {
  //   return Promise.resolve({
  //     "value":"New file.txt","id":"/New file.txt","size":0,
  //     "date":1584635214,"type":"code"
  //   });
  // }

  // Returns the path to a file icon. The super methods returns links to material design icons. TODO how to integrate them?
  // We currently return an icon of our font. TODO This should be styled to our theme.
  icon(file, size) {
    // const result = super.icon(file, size);
    // console.log(result);
    // return result;
    return "/assets/icons/file.svg";
  }

  // Moves a file or folder.
  // POST move
  move(from: string, toFolder: string) {
    const file = from.includes("/") ? from.substring(from.lastIndexOf("/") + 1) : from;
    if (!toFolder.endsWith("/")) {
      toFolder = toFolder + "/";
    }
    const hrefFiles = this.apiService.resolveLink(this.storage._links["fo:move"].href, { from: from, to: toFolder + file });
    return this.apiService
      .post(hrefFiles, null)
      .toPromise()
      .then((serverFile) => {
        return mapFile(serverFile);
      })
      .catch(this.errorHandler);
  }

  // Do not implement. Returns a url to the generated preview of a file.
  // previewURL(path, width, height) {
  //   return "http://localhost:3200/preview?width=464&height=407&id=%2Fimage.jpg";
  // }

  // Maybe implement. Returns the content of a text file for editing.
  // GET download
  // readText(textFilePath) {
  //   return Promise.resolve("content");
  // }

  // Removes a file or directory.
  remove(path: string) {
    const hrefFiles = this.apiService.resolveLink(this.storage._links["fo:delete"].href, { path: path });
    return this.apiService
      .delete(hrefFiles, null)
      .toPromise()
      .then(() => {
        return {
          invalid: false,
          error: "",
          id: "",
        };
      })
      .catch(this.errorHandler);
  }

  // Renames a file or directory.
  rename(from: string, to: string) {
    const hrefFiles = this.apiService.resolveLink(this.storage._links["fo:move"].href, { from: from, to: to });
    return this.apiService
      .post(hrefFiles, null)
      .toPromise()
      .then((serverFile) => {
        return mapFile(serverFile);
      })
      .catch(this.errorHandler);
  }

  // Returns files and folders matching the search string and filter.
  // Am besten den Filter in der Config deaktivieren, sonst wird das zu aufwändig.
  // GET search
  // search(path, searchString, filter) {
  // return Promise.resolve([{
  //     "value":"move.txt","id":"/New folder/move.txt","size":0,
  //     "date":1584635665,"type":"code"
  //   }]);
  // }

  // Returns a link to upload a file. Dummy method, this is dynamically set in UploadAdapter.
  upload() {
    return "upload";
  }

  // Maybe implement. Writes the contents of a text file after editing.
  // POST upload
  // writeText(textFilePath, content) {
  //   return Promise.resolve({
  //     "value":"write.txt","id":"/write.txt","size":42,
  //     "date":1584632624,"type":"code"
  //   });
  // }
}

class OperationsAdapter extends fileManager.services.Operations {
  fileService: FilesService;
  storageComponent: DataStorageComponent;
  reader = new FileReader();

  private readonly showSpinner = (show: boolean) => {
    this.storageComponent && (this.storageComponent.showSpinner = show);
  };

  download(items: string) {
    this.showSpinner(true);
    // @ts-ignore
    const link = this.backend().directLink(items[0].id);
    this.fileService
      .downloadFileLink(link)
      .toPromise()
      .catch((err) => {
        this.reader.onload = (event) => {
          const jsonData = JSON.parse(event.target.result as string);
          this.storageComponent.showError(jsonData.message);
        };
        this.reader.readAsText(err.error);
      })
      .finally(() => this.showSpinner(false));
  }

  makeFolder(newFolderName: string) {
    const path: any = (this as any).state.path || "/";
    // @ts-ignore
    this.backend()
      .makedir(path, newFolderName)
      .then((file) => {
        // @ts-ignore
        file.invalid || this.local().addFile(path, file);
      });
  }

  open(items: string) {
    this.download(items);
  }
}

class UploadAdapter extends fileManager.services.Upload {
  storage: DataStorageComponent;
  links: any;

  readonly setUploadEndPoint = (uploader) => {
    const link = this.links["fo:upload"].href;
    // @ts-ignore
    uploader.config.upload = link.substring(0, link.indexOf("?"));
  };
  readonly refresh = () => this.storage.refreshFiles();

  initEvents(app, state) {
    super.initEvents(app, state);
    // @ts-ignore
    this.uploader.attachEvent("onAfterFileAdd", () => this.setUploadEndPoint(this.uploader));
    // @ts-ignore
    this.uploader.attachEvent("onUploadComplete", (response) => this.refresh());
    // @ts-ignore
    this.dirUploader.attachEvent("onAfterFileAdd", () => this.setUploadEndPoint(this.dirUploader));
    // @ts-ignore
    // this.dirUploader.attachEvent("onUploadComplete", (response) => this.refresh());

    function getUploadErrorMessage(file: any): string {
      const json = JSON.parse(file.xhr.response);
      return json.message ?? "Upload failed.";
    }

    // @ts-ignore
    this.uploader.attachEvent("onFileUploadError", (file: any, response: any) => {
      const msg = getUploadErrorMessage(file);
      this.storage.showError(msg);
    });

    // @ts-ignore
    this.dirUploader.attachEvent("onFileUploadError", (file: any, response: any) => {
      const msg = getUploadErrorMessage(file);
      this.storage.showError(msg);
    });
  }
}

//von https://docs.webix.com/filemanager__customization.html
class CustomTopBar extends fileManager.views.topbar {
  init() {
    super.init();
    // möglichkeiten sind:search,filterButton,previewMode,modes,filterBar
    (this as any).$$("search").hide();
    (this as any).$$("previewMode").hide();
  }
}

// von https://docs.webix.com/filemanager__howto.html#addingabottombar
class CustomAddNewMenu extends fileManager.views["menus/addnewmenu"] {
  init(view) {
    super.init();
    //mögliche Aktionen: makefile, makedir, uploaddir, upload
    view.queryView("menu").remove("makefile");
  }
}

class CustomContextMenu extends fileManager.views["menus/contextmenu"] {
  init(view) {
    super.init();

    const popup: any = view || (this as any).getRoot();
    popup.attachEvent("onBeforeShow", () => {
      const menu: any = popup.queryView("menu") || popup;
      const separator = (it: any) => it?.$template === "Separator";

      const items = menu.data.serialize();
      items.filter((it: any) => separator(it)).forEach((it: any) => menu.data.remove(it.id));

      if (menu && menu.data) {
        //Mögliche Aktionen: copy, cut, delete, download, open, paste, rename, toggle-preview
        ["copy", "open", "toggle-preview"].forEach((id) => {
          if (menu.data.exists(id)) menu.data.remove(id);
        });
      }
    });
  }
}

@Component({
  selector: "app-data-storage",
  templateUrl: "./data-storage.component.html",
  styleUrls: ["./data-storage.component.scss"],
})
export class DataStorageComponent implements OnDestroy, OnInit {
  selectedVessel: any;
  showSpinner: boolean = false;

  private subscriptions: Subscription[] = [];
  private ui: webix.ui.filemanager = null;
  private app = null;
  private fileManagerAdapter: FileManagerAdapter = null;
  private uploadAdapter: UploadAdapter = null;
  private operationsAdapter: OperationsAdapter = null;
  // private resizeId: number = 0;
  private fmEl: HTMLElement = null;
  private ro: ResizeObserver = null;

  constructor(
    private apiService: ApiService,
    private dataStorageService: DataStorageService,
    private vesselGroupsService: VesselGroupsService,
    private fileService: FilesService,
    private messageService: MessageService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    const overrides = new Map();
    overrides.set(fileManager.services.Backend, FileManagerAdapter);
    overrides.set(fileManager.services.Upload, UploadAdapter);
    overrides.set(fileManager.services.Operations, OperationsAdapter);
    overrides.set(fileManager.views.topbar, CustomTopBar);
    overrides.set(fileManager.views["menus/addnewmenu"], CustomAddNewMenu);
    overrides.set(fileManager.views["menus/contextmenu"], CustomContextMenu);

    webix.ready(() => {
      this.ui = webix.ui({
        view: "filemanager",
        container: "webix-fm",
        enableFilter: false,
        override: new Map(overrides),
        url: "http://localhost:3200/",
        on: {
          onInit: (app) => {
            this.app = app;
            this.fileManagerAdapter = app.getService("backend");
            this.fileManagerAdapter.apiService = this.apiService;
            this.fileManagerAdapter.storageComponent = this;
            this.uploadAdapter = app.getService("upload");
            this.uploadAdapter.storage = this;
            this.operationsAdapter = app.getService("operations");
            this.operationsAdapter.fileService = this.fileService;
            this.operationsAdapter.storageComponent = this;
          },
        },
      });
      webix.attachEvent("onBeforeAjax", (mode, url, data, request, headers, files, promise) => {
        const authorization = this.apiService.getAuthorizationHeaders();
        headers["Authorization"] = authorization["Authorization"];
      });
    });

    const onResize = () => this.ui?.resize();
    if ("fonts" in document) {
      (document as any).fonts.ready.then(onResize);
    }
    this.fmEl = document.getElementById("webix-fm");
    if (this.fmEl && "ResizeObserver" in window) {
      this.ro = new ResizeObserver(() => onResize());
      this.ro.observe(this.fmEl);
    }
    // this.resizeId = webix.event(window, "resize", onResize);

    this.apiService
      .initialize()
      .toPromise()
      .then(() => {
        this.subscriptions.push(
          this.vesselGroupsService.getSelectedVesselObservable().subscribe({
            next: (selectedVessel) => {
              this.selectedVessel = selectedVessel;
              if (this.fileManagerAdapter) {
                this.fileManagerAdapter.selectedVessel = this.selectedVessel;
              }
              if (!this.selectedVessel) {
                this.clearFiles();
                this.ui.disable();
              }
            },
          }),
          this.dataStorageService.initialize().subscribe({
            next: (storage) => {
              if (this.fileManagerAdapter) {
                this.fileManagerAdapter.storage = storage;
              }
              if (this.uploadAdapter) {
                this.uploadAdapter.links = storage?._links ?? undefined;
              }
              if (storage) {
                this.ui.enable();
                this.refreshFiles();
              } else {
                this.ui.disable();
                this.clearFiles();
              }
              this.app.callEvent("reload:fs:stats", []);
            },
          })
        );
      });
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.ro.unobserve(this.fmEl);
    // webix.eventRemove(this.resizeId);
    this.ui.destructor();
  }

  getSelectedVesselId(): number {
    return this.vesselGroupsService.getSelectedVesselId();
  }

  getVesselName() {
    return this.selectedVessel ? this.selectedVessel.name : "";
  }

  refreshFiles() {
    this.app.getService("local").folders(true);
    this.app.getService("local").refresh(this.ui.getState().path);
  }

  private clearFiles() {
    this.ui.getState().path = "/";
    this.app
      .getService("local")
      .folders()
      .then((collection) => collection.clearAll());
    this.app.getService("local").fscache.each((collection) => collection.clearAll());
  }

  showError(msg: string): void {
    this.zone.run(() => {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: msg || "Unknown error",
        life: 6000,
      });
    });
  }
}
